import React, { useEffect, useRef } from 'react';

const DonutChart = ({ data = [], size = 160, thickness = 0.35, showLabels = true }) => {
  const ref = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cx = size / 2, cy = size / 2;
    const r = size / 2 - 10;
    const inner = r * (1 - thickness);
    const total = data.reduce((s, d) => s + Math.max(0, d.value), 0) || 1;

    let prog = 0;
    const animate = () => {
      prog = Math.min(1, prog + 0.04);
      ctx.clearRect(0, 0, size, size);

      let start = -Math.PI / 2;
      data.forEach((slice) => {
        const sweep = (Math.max(0, slice.value) / total) * Math.PI * 2 * prog;
        const color = slice.color || '#3b82f6';

        // Shadow
        ctx.shadowColor = `${color}44`;
        ctx.shadowBlur = 12;

        ctx.beginPath();
        ctx.arc(cx, cy, r, start, start + sweep);
        ctx.arc(cx, cy, inner, start + sweep, start, true);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
        ctx.shadowBlur = 0;

        // White separator
        ctx.beginPath();
        ctx.arc(cx, cy, r, start, start + sweep);
        ctx.arc(cx, cy, inner, start + sweep, start, true);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        start += sweep;
      });

      // Center bg
      const centerGrad = ctx.createRadialGradient(cx, cy - 8, 0, cx, cy, inner);
      centerGrad.addColorStop(0, 'rgba(248,250,252,0.05)');
      centerGrad.addColorStop(1, 'rgba(15,23,42,0.0)');
      ctx.beginPath();
      ctx.arc(cx, cy, inner - 1, 0, Math.PI * 2);
      ctx.fillStyle = centerGrad;
      ctx.fill();

      if (prog < 1) raf.current = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(raf.current);
  }, [data, size, thickness]);

  const total = data.reduce((s, d) => s + Math.max(0, d.value), 0);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <canvas ref={ref} style={{ width: size, height: size }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 20, color: '#0f172a' }}>{total >= 1000 ? `${(total/1000).toFixed(1)}k` : total}</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>Total</div>
        </div>
      </div>
      {showLabels && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.map((d, i) => {
            const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
            return (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                    <span style={{ color: '#475569' }}>{d.label}</span>
                  </span>
                  <span style={{ fontWeight: 400, color: '#0f172a' }}>{pct}%</span>
                </div>
                <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: d.color, borderRadius: 2, transition: 'width 0.8s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
